import React, { useState } from 'react';

interface CollapsibleThinkBlockProps {
  content: string;
  theme: 'light' | 'dark';
}

export const CollapsibleThinkBlock: React.FC<CollapsibleThinkBlockProps> = ({ content, theme }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const containerStyle: React.CSSProperties = {
    margin: '8px 0',
    borderRadius: '6px',
    backgroundColor: theme === 'light' ? '#F0F4F9' : '#1E2937',
    border: `1px solid ${theme === 'light' ? '#E2E8F0' : '#374151'}`,
    overflow: 'hidden',
    transition: 'all 0.2s ease',
  };

  const headerStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderLeft: `4px solid ${theme === 'light' ? '#3B82F6' : '#60A5FA'}`,
    backgroundColor: theme === 'light' ? '#F0F4F9' : '#1E2937',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    userSelect: 'none',
    transition: 'background-color 0.2s ease',
  };

  const headerHoverStyle: React.CSSProperties = {
    backgroundColor: theme === 'light' ? '#E2E8F0' : '#253141',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '0.85em',
    color: theme === 'light' ? '#4B5563' : '#9CA3AF',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const iconStyle: React.CSSProperties = {
    width: '16px',
    height: '16px',
    transition: 'transform 0.2s ease',
    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
    color: theme === 'light' ? '#6B7280' : '#9CA3AF',
  };

  const contentStyle: React.CSSProperties = {
    padding: isExpanded ? '0 16px 16px 20px' : '0',
    maxHeight: isExpanded ? '500px' : '0',
    overflow: 'auto',
    transition: 'all 0.3s ease',
    opacity: isExpanded ? 1 : 0,
  };

  const preStyle: React.CSSProperties = {
    fontFamily: 'inherit',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    margin: 0,
    fontSize: '0.95em',
    fontStyle: 'italic',
    color: theme === 'light' ? '#1F2937' : '#E5E7EB',
    lineHeight: '1.5',
  };

  const previewStyle: React.CSSProperties = {
    fontSize: '0.85em',
    color: theme === 'light' ? '#6B7280' : '#9CA3AF',
    fontStyle: 'italic',
    marginLeft: '8px',
  };

  // Get preview text (first 50 characters)
  const getPreview = () => {
    if (isExpanded) return '';
    const preview = content.slice(0, 60).replace(/\n/g, ' ');
    return preview.length < content.length ? `${preview}...` : preview;
  };

  const [isHovered, setIsHovered] = useState(false);

  return (
    <div style={containerStyle}>
      <div 
        style={{ ...headerStyle, ...(isHovered ? headerHoverStyle : {}) }}
        onClick={() => setIsExpanded(!isExpanded)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={titleStyle}>
          <svg 
            style={iconStyle} 
            viewBox="0 0 16 16" 
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
          <span>Thinking process</span>
          {!isExpanded && <span style={previewStyle}>{getPreview()}</span>}
        </div>
      </div>
      <div style={contentStyle}>
        <pre style={preStyle}>{content}</pre>
      </div>
    </div>
  );
};